import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Accordion, AccordionBody, AccordionHeader, CardBody, Checkbox} from "@material-tailwind/react";
import {API} from "../../types/API";
import {AxiosResponse} from "axios";
import Button from "../../components/Button";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {UserListResponse} from "../../types/Responses/UsersResponse";
import {PaymentMethodCurrencyResponse, PaymentResponse} from "../../types/Responses/VirtualTerminalResponse";
import {useSystemContext} from "../../types/contexts/SystemContext";
import DropdownInput from "../../components/DropdownInput";
import OutlineInput from "../../components/OutlineInput";
import {CountriesShortResponse} from "../../types/Responses/CountriesResponse";
import CountryAvatar from "../../components/CountryAvatar";
import FormSkeleton from "../../components/Skeletons/FormSkeleton";
import {useUserContext} from "../../types/contexts/UserContext";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {SourceShortResponse} from "../../types/Responses/SourcesResponse";
import {APILink} from "../../types/APILink";
import {checkEnabled2FA} from "../../hooks/checkEnabled2FA";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../reducers/sidebar";
import {
    clearRememberedValues,
    loadRememberedValues,
    RememberedValues,
    saveRememberedValues
} from "./virtualTerminalStorage";

const VirtualTerminal = () => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const system = useSystemContext();
    const user = useUserContext();
    const dispatch = useDispatch();
    const [remembered] = useState<RememberedValues | null>(loadRememberedValues);
    const [rememberValues, setRememberValues] = useState<boolean>(remembered !== null);
    // Restored select values are checked against the option lists once those have loaded.
    const restorePending = useRef<boolean>(remembered !== null);
    const [filterBroker, setFilterBroker] = useState<number | undefined>(remembered?.broker);
    const [filterPaymentMethod, setFilterPaymentMethod] = useState<number | undefined>(remembered?.paymentMethod);
    const [filterCurrency, setFilterCurrency] = useState<number | undefined>(remembered?.currency);
    const [filterLanguage, setFilterLanguage] = useState<number | undefined>(remembered?.language);
    const [filterTheme, setFilterTheme] = useState<string | undefined>(remembered?.theme);
    const [filterCountry, setFilterCountry] = useState<number | undefined>(remembered?.country);
    const [filterSource, setFilterSource] = useState<number>(remembered?.source ?? -1);
    const [amount, setAmount] = useState<number>(remembered?.amount ?? 1);
    const [phone, setPhone] = useState<string>(remembered?.phone ?? '+35795000000');
    const [address, setAddress] = useState<string>(remembered?.address ?? 'Test address');
    const [zip, setZip] = useState<string>(remembered?.zip ?? '3001');
    const [email, setEmail] = useState<string | undefined>(remembered?.email);
    const [city, setCity] = useState<string>(remembered?.city ?? 'Limassol');
    const [firstName, setFirstName] = useState<string>(remembered?.firstName ?? 'Virtual');
    const [lastName, setLastName] = useState<string>(remembered?.lastName ?? 'Terminal');
    const [openAccordion, setOpenAccordion] = useState<boolean>(false)
    const [brokers, setBrokers] = useState<UserListResponse>();
    const [countriesList, setCountriesList] = useState<CountriesShortResponse>();
    const [paymentMethodCurrencies, setPaymentMethodCurrencies] = useState<PaymentMethodCurrencyResponse>();
    const [sourcesList, setSourcesList] = useState<SourceShortResponse>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const sendRequest = useCallback(() => {
        setIsLoading(true);
        API.emulatePayment({
            broker_id: filterBroker,
            payment_method_id: filterPaymentMethod,
            country_id: filterCountry,
            currency_id: filterCurrency,
            language_id: filterLanguage,
            source_id: filterSource,
            theme: filterTheme,
            phone,
            amount,
            zip,
            first_name: firstName,
            last_name: lastName,
            city,
            email,
            address
        }).then((response: AxiosResponse) => {
            API.makePayment(response.data, response.headers['x-sanctum-auth-token']).then((response: AxiosResponse) => {
                const paymentResponse = response.data.data as PaymentResponse;
                setIsLoading(false);
                window.location.replace(paymentResponse.payment_page)
            }).catch(e => {
                ErrorHandler(e, `post${APILink.INIT_PAYMENT}`, toast, handleError);
                setIsLoading(false);
            });
        }).catch(e => {
            ErrorHandler(e, `post${APILink.EMULATE_PAYMENT}`, toast, handleError);
            setIsLoading(false);
        });
    }, [filterBroker, filterPaymentMethod, filterCountry, filterCurrency, filterLanguage, filterSource, filterTheme, phone, amount, zip, firstName, lastName, city, email, address, toast, handleError]);

    const setupLanguageAndTheme = useCallback(() => {
        if (!system || !system.system || !system.system.themes || !system.system.languages || !user || !user.user)
            return;
        // Restored values take precedence over the user's own theme and language.
        const theme = user.user.theme;
        const languageId = system.system.languages.find(l => l.code === user.user.language)?.id;
        setFilterTheme(current => current ?? theme)
        setFilterLanguage(current => current ?? languageId)
    }, [system, user]);

    const getCountries = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            setCountriesList(response.data as CountriesShortResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const getSources = useCallback(() => {
        API.getShortSources().then((response: AxiosResponse) => {
            setSourcesList(response.data as SourceShortResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_SHORT_SOURCES}`, toast, handleError));
    }, [toast, handleError]);

    const getBrokers = useCallback(() => {
        API.getBrokersList().then((response: AxiosResponse) => {
            setBrokers(response.data as UserListResponse);
            if (response.data.data.length === 1) {
                setFilterBroker(response.data.data[0].id);
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKERS_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const getPaymentMethods = useCallback(() => {
        API.getPaymentMethodCurrenciesList().then((response: AxiosResponse) => {
            setPaymentMethodCurrencies(response.data as PaymentMethodCurrencyResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYMENT_METHOD_CURRENCIES_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const onUserChanged = useCallback((u: string | number) => {
        setFilterBroker(typeof u === "string" ? parseInt(u) : u);
    }, [setFilterBroker]);

    const onPaymentMethodChanged = useCallback((u: string | number) => {
        if (paymentMethodCurrencies && filterBroker) {
            const id = typeof u === "string" ? parseInt(u) : u;
            setFilterPaymentMethod(id);
            const pm = paymentMethodCurrencies.data.filter(p => p.data?.map(d => d.broker).includes(filterBroker ?? 0)).find((p) => parseInt(p.payment_method.id) === id) ?? null;
            if (pm && pm.payment_method.params) {
                const params = pm.payment_method.params;
                if (params.address)
                    setAddress(params.address);
                if (params.phone)
                    setPhone(params.phone);
                if (params.city)
                    setCity(params.city);
                if (params.zip)
                    setZip(params.zip);
            }
        }
    }, [setFilterPaymentMethod, filterBroker, paymentMethodCurrencies]);

    const onCurrencyChanged = useCallback((u: string | number) => {
        const currency = typeof u === "string" ? parseInt(u) : u;
        setFilterCurrency(currency);
        if (paymentMethodCurrencies && filterPaymentMethod && filterBroker) {
            const row = paymentMethodCurrencies.data.find(p => p.payment_method.id.toString() === filterPaymentMethod.toString())?.data?.find(c => c.broker === filterBroker && c.country === filterCountry && c.currency === currency);
            if (row) {
                setAmount(row.min);
            }
        }
    }, [setFilterCurrency, setAmount, paymentMethodCurrencies, filterCountry, filterPaymentMethod, filterBroker]);

    const onLanguageChanged = useCallback((u: string | number) => {
        setFilterLanguage(typeof u === "string" ? parseInt(u) : u);
    }, [setFilterLanguage]);

    const onThemeChanged = useCallback((u: string | number) => {
        setFilterTheme(typeof u === "string" ? u : u.toString());
    }, [setFilterTheme]);

    const onSourceChanged = useCallback((u: string | number) => {
        setFilterSource(typeof u === "string" ? parseInt(u) : u);
    }, [setFilterSource]);

    const onCountryChanged = useCallback((u: string | number) => {
        setFilterCountry(typeof u === "string" ? parseInt(u) : u);
    }, [setFilterCountry]);

    const onAmountChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setAmount]);

    const onPhoneChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setPhone(e.currentTarget.value);
        }
    }, [setPhone]);

    const onZipChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setZip(e.currentTarget.value);
        }
    }, [setZip]);

    const onAddressChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setAddress(e.currentTarget.value);
        }
    }, [setAddress]);

    const onCityChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setCity(e.currentTarget.value);
        }
    }, [setCity]);

    const onFirstNameChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setFirstName(e.currentTarget.value);
        }
    }, [setFirstName]);

    const onLastNameChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setLastName(e.currentTarget.value);
        }
    }, [setLastName]);

    const onEmailChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setEmail(e.currentTarget.value);
        }
    }, [setEmail]);

    useEffect(() => {
        setTitle(t('admin.virtual_terminal.title'));
        dispatch(setSidebarTitle(t('admin.virtual_terminal.title')))
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                setEmail(e => e ?? 'virtualterminal@' + window.location.hostname);
                getPaymentMethods();
                getBrokers();
                getCountries();
                getSources();
                setupLanguageAndTheme();
            }).catch();
        }
    }, [user, dispatch, setSidebarTitle]);

    useEffect(() => {
        if (rememberValues) {
            saveRememberedValues({
                broker: filterBroker, paymentMethod: filterPaymentMethod, currency: filterCurrency,
                language: filterLanguage, theme: filterTheme, country: filterCountry, source: filterSource,
                amount, phone, address, zip, email, city, firstName, lastName
            });
        } else {
            clearRememberedValues();
        }
    }, [rememberValues, filterBroker, filterPaymentMethod, filterCurrency, filterLanguage, filterTheme, filterCountry, filterSource, amount, phone, address, zip, email, city, firstName, lastName]);

    // Drops restored select values that are no longer offered (broker, payment method, country
    // or currency removed or reconfigured since they were stored), so they are neither shown nor submitted.
    // Each select depends on the one before it, so an invalid value also clears everything after it.
    useEffect(() => {
        if (!restorePending.current || !brokers || !paymentMethodCurrencies || !sourcesList || !user || !user.user
            || !system || !system.system || !system.system.languages || !system.system.themes)
            return;
        restorePending.current = false;

        const brokerValid = brokers.data.some(b => parseInt(b.id) === filterBroker);
        const rows = brokerValid ? paymentMethodCurrencies.data
            .find(p => p.payment_method.id.toString() === filterPaymentMethod?.toString())?.data
            ?.filter(c => c.broker === filterBroker) ?? [] : [];
        const countryValid = rows.some(c => c.country === filterCountry);
        const currencyValid = countryValid && rows.some(c => c.country === filterCountry && c.currency === filterCurrency);
        if (!brokerValid)
            setFilterBroker(undefined);
        if (rows.length === 0)
            setFilterPaymentMethod(undefined);
        if (!countryValid)
            setFilterCountry(undefined);
        if (!currencyValid)
            setFilterCurrency(undefined);

        if (!system.system.languages.some(l => l.id === filterLanguage))
            setFilterLanguage(system.system.languages.find(l => l.code === user.user.language)?.id);
        if (!system.system.themes.some(th => th.name === filterTheme))
            setFilterTheme(user.user.theme);
        if (filterSource !== -1 && !sourcesList.data.some(s => s.id === filterSource))
            setFilterSource(-1);
    }, [brokers, paymentMethodCurrencies, sourcesList, system, user, filterBroker, filterPaymentMethod, filterCountry, filterCurrency, filterLanguage, filterTheme, filterSource]);

    const filters = useMemo(() => {
        if (!brokers || !paymentMethodCurrencies || !system || !system.system || !system.system.currencies || !countriesList)
            return <FormSkeleton/>;

        const paymentMethods = filterBroker ? paymentMethodCurrencies.data.filter(p => p.data?.map(d => d.broker).includes(filterBroker ?? 0)).map(p => p.payment_method.id) ?? [] : [];

        const countries = filterPaymentMethod ?
            paymentMethodCurrencies.data.find(p => p.payment_method.id.toString() === filterPaymentMethod.toString())?.data?.filter(c => c.broker === filterBroker).map(c => c.country) ?? []
            : [];

        const currencies = filterPaymentMethod ?
            paymentMethodCurrencies.data.find(p => p.payment_method.id.toString() === filterPaymentMethod.toString())?.data?.filter(c => c.broker === filterBroker && c.country === filterCountry).map(c => c.currency) ?? []
            : [];

        const row = filterPaymentMethod ? paymentMethodCurrencies.data.find(p => p.payment_method.id.toString() === filterPaymentMethod.toString())?.data?.find(c => c.broker === filterBroker && c.country === filterCountry && c.currency === filterCurrency) : null;

        return <div className="grid gird-cols-1 lg:grid-cols-2 gap-5">
            {brokers.data.length > 1 ?
                <DropdownInput name={t('admin.virtual_terminal.table.broker')} list={brokers.data.map((u) => {
                    return {name: u.name, id: parseInt(u.id)}
                })} selected={filterBroker} searchable={true} onChange={onUserChanged}/> : ''}

            {filterBroker ?
                <DropdownInput
                    list={paymentMethodCurrencies.data.filter(p => paymentMethods.includes(p.payment_method.id)).map(p => {
                        const name = paymentMethodCurrencies.data.filter(s => s.payment_method.name === p.payment_method.name && s.payment_method.id !== p.payment_method.id).length > 0
                            ? `${p.payment_method.name} ${p.payment_method.provider_name}` : p.payment_method.name;
                        return {name, id: parseInt(p.payment_method.id)}
                    })} name={t('admin.virtual_terminal.table.payment_method')} searchable={true}
                    selected={filterPaymentMethod}
                    onChange={onPaymentMethodChanged}/> : ''}

            {filterPaymentMethod ?
                <DropdownInput
                    list={countriesList.data.filter(c => countries.includes(c.id)).map((c) => {
                        return {
                            name: c.name, id: c.id, icon: <CountryAvatar country={c} withName={false}
                                                                         className="h-3 max-w-[1rem]"/>
                        }
                    })}
                    name={t('admin.virtual_terminal.table.country')} selected={filterCountry} searchable={true}
                    onChange={onCountryChanged}/> : ''}

            {filterCountry ?
                <DropdownInput
                    list={system.system.currencies.filter(c => currencies.includes(c.id)).map((c) => {
                        return {name: c.code, id: c.id}
                    })}
                    name={t('admin.virtual_terminal.table.currency')} selected={filterCurrency} searchable={true}
                    onChange={onCurrencyChanged}/> : ''}

            {filterCurrency ?
                <OutlineInput validated={false} name={row ? t('admin.virtual_terminal.table.amount_between', {
                    min: row.min,
                    max: row.max
                }) : t('admin.virtual_terminal.table.amount')}
                              value={amount} fieldName="amount" type="number"
                              onChange={onAmountChanged}/> : ''}
            {filterCurrency ?
                <OutlineInput validated={false} name={t('admin.virtual_terminal.table.phone')} value={phone}
                              fieldName="phone" type="text" onChange={onPhoneChanged}/> : ''}
        </div>
    }, [brokers, system, amount, phone, sourcesList, paymentMethodCurrencies, filterCountry, filterPaymentMethod, filterBroker, filterCurrency, onUserChanged, onPaymentMethodChanged, onAmountChanged, onCurrencyChanged, onCountryChanged, onPhoneChanged]);

    const buttonUI = useMemo(() => {
        if (!filterCurrency || !amount)
            return <></>;

        return <Button onClick={() => sendRequest()} name={t('Submit')}
                       withProgressBar={true}
                       type="submit"
                       isInProgress={isLoading}
                       className="bg-teal-500 text-white" relativeClassName="mt-6"/>;

    }, [filterCurrency, amount, filterCountry, filterPaymentMethod, isLoading, filterLanguage, filterCurrency, filterTheme, filterBroker, filterSource, sendRequest]);

    const extraFieldsUI = useMemo(() => {
        if (!filterCurrency || !amount || !system || !system.system || !system.system.languages || !system.system.themes || !sourcesList)
            return <></>;

        const sources = [
            {
                id: -1,
                name: 'None',
            },
            ...sourcesList.data.map((s) => {
                return {id: s.id, name: s.source};
            })
        ];
        return <Accordion className="w-full"
                          open={openAccordion}>
            <AccordionHeader className="w-full font-medium text-[1rem] dark:text-gray-400"
                             onClick={() => setOpenAccordion(!openAccordion)}>{t('admin.virtual_terminal.table.extra_params')}</AccordionHeader>
            <AccordionBody className={`${openAccordion ? `make-visible-accordion` : ''}`}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <DropdownInput
                        id="payment_language"
                        list={system.system.languages.map((l) => {
                            return {
                                name: l.name, id: l.id, icon: <CountryAvatar country={l} withName={false}
                                                                             className="h-3 max-w-[1rem]"/>
                            }
                        })}
                        name={t('admin.virtual_terminal.table.language')} selected={filterLanguage} searchable={true}
                        onChange={onLanguageChanged}/>
                    <DropdownInput
                        id="payment_theme"
                        list={system.system.themes}
                        name={t('admin.virtual_terminal.table.theme')} selected={filterTheme}
                        onChange={onThemeChanged}/>
                    <DropdownInput
                        id="payment_source"
                        list={sources}
                        name={t('admin.virtual_terminal.table.source')} selected={filterSource}
                        onChange={onSourceChanged}/>
                    <OutlineInput validated={false} name={t('admin.virtual_terminal.table.address')} value={address}
                                  fieldName="address" type="text" onChange={onAddressChanged}/>
                    <OutlineInput validated={false} name={t('admin.virtual_terminal.table.city')} value={city}
                                  fieldName="city" type="text" onChange={onCityChanged}/>
                    <OutlineInput validated={false} name={t('admin.virtual_terminal.table.email')} value={email}
                                  fieldName="email" type="text" onChange={onEmailChanged}/>
                    <OutlineInput validated={false} name={t('admin.virtual_terminal.table.zip')} value={zip}
                                  fieldName="zip" type="text" onChange={onZipChanged}/>
                    <OutlineInput validated={false} name={t('admin.virtual_terminal.table.first_name')}
                                  value={firstName}
                                  fieldName="firstName" type="text" onChange={onFirstNameChanged}/>
                    <OutlineInput validated={false} name={t('admin.virtual_terminal.table.last_name')} value={lastName}
                                  fieldName="lastName" type="text" onChange={onLastNameChanged}/>
                </div>
            </AccordionBody>
        </Accordion>
    }, [filterCurrency, filterLanguage, filterTheme, amount, address, city, email, zip, firstName, lastName, openAccordion, system, onLanguageChanged, onThemeChanged, sourcesList, filterSource, onSourceChanged, onLastNameChanged, onFirstNameChanged, onAddressChanged, onEmailChanged, onCityChanged, onZipChanged]);

    return (
        <PageBlock>
            <Card id="virtual_terminal_card" className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {filters}
                    {extraFieldsUI}
                    <Checkbox checked={rememberValues}
                              onChange={e => setRememberValues(e.target.checked)}
                              color={'teal'} crossOrigin={''}
                              label={<span className="dark:text-gray-500">{t('Remember these values')}</span>}/>
                    {buttonUI}
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(VirtualTerminal);
